import NoPhotoImg from "assets/images/user.png";
import dagre from "dagre";
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Handle, Position } from "reactflow";
import PropTypes from "prop-types";
import { getBezierPath, getEdgeCenter, getStraightPath } from "reactflow";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { getRiskMatrices, getEmployeePerformanceDistribution } from "services/api";
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  HelpOutline,
  Flag,
  CloudDownload,
  TrackChanges,
  CheckCircle,
  ErrorOutlineRounded,
  Cancel,
  Circle,
  CheckCircleOutline,
  Close as CloseIcon,
} from "@mui/icons-material";
import { FiberManualRecord } from "@mui/icons-material";
import { useAppTheme } from "context/themeContext";
import theme from "assets/theme";

export const calculateKpiResult = (
  target,
  primaryAchievement,
  secondaryAchievement,
  formula,
  primaryPercentage = 100,
  weight = null
) => {
  // Validate inputs
  if (target == null || target === 0) return null;
  if (primaryAchievement == null && secondaryAchievement == null) return null;

  // Normalize formula
  const normalizedFormula = (formula || "regular").trim().toLowerCase();

  // Calculate blend ratios (ensure 0-100 range)
  const effectivePrimaryPct = Math.max(0, Math.min(primaryPercentage, 100));
  const primaryRatio = effectivePrimaryPct / 100;
  const secondaryRatio = 1 - primaryRatio;

  // Calculate individual scores (0-100 scale)
  const primaryScore =
    primaryAchievement != null
      ? calculateIndividualScore(target, primaryAchievement, normalizedFormula)
      : 0;

  const secondaryScore =
    secondaryAchievement != null
      ? calculateIndividualScore(target, secondaryAchievement, normalizedFormula)
      : 0;

  // Blend scores according to percentages
  let blendedScore = 0;
  blendedScore = primaryScore * primaryRatio + secondaryScore * secondaryRatio;

  // Prevent the blended score from going above 100% in case of errors

  // Handle Infinity cases
  if (!isFinite(blendedScore)) return null;

  // Return appropriate result
  return weight != null
    ? (blendedScore / 100) * weight // Weighted mode (convert % to ratio first)
    : blendedScore; // Unweighted mode (0-100 scale)
};

// Helper function to calculate individual scores (0-100 scale)
const calculateIndividualScore = (target, achievement, formula) => {
  if (target === 0) return 0; // Prevent division by zero

  const percentage =
    formula === "reverse"
      ? ((target - achievement) / target) * 100 + 100
      : ((achievement - target) / target) * 100 + 100;

  // Clamp between 0-200% to prevent extreme values
  return Math.min(percentage, 1000);
};

export const calculateAverageLocation = (branches) => {
  if (!branches || branches.length === 0) {
    return [26.8206, 30.8025]; // Default to Egypt's coordinates if no branches are provided
  }

  const total = branches.reduce(
    (acc, branch) => {
      return {
        lat: acc.lat + branch.latitude,
        lng: acc.lng + branch.longitude,
      };
    },
    { lat: 0, lng: 0 }
  );

  const avgLat = total.lat / branches.length;
  const avgLng = total.lng / branches.length;
  return [avgLat, avgLng];
};

export const detectDataType = (columns) => {
  const normalized = columns.map((col) => col.toLowerCase());

  const has = (...cols) => cols.every((c) => normalized.includes(c.toLowerCase()));

  if (has("Area Name", "Area ID", "Latitude", "Longitude")) return "areas";
  if (has("Branch ID", "Branch Name", "Address", "Area Name", "Latitude", "Longitude"))
    return "branches";
  if (has("Job ID", "Job Title", "Department Name", "Reporting To Solid", "Reporting To Dotted"))
    return "jobs";
  if (has("Department Name", "Department ID")) return "departments";
  if (
    has(
      "ID",
      "Name",
      "Email",
      "Phone Number",
      "Referenced ID",
      "Role",
      "Gender",
      "Branch",
      "Department",
      "Job",
      "Direct Manager",
      "Indirect Manager"
    )
  )
    return "employees";
  if (
    has(
      "KPI ID",
      "KPI Name",
      "Perspective",
      "KPI Formula",
      "Description",
      "Theme",
      "Accountability",
      "Units",
      "Generated From",
      "Source"
    )
  )
    return "kpis";
  if (
    has(
      "Job",
      "Branch",
      "KPI",
      "Primary Assessor",
      "Secondary Assessor",
      "Weight",
      "Target",
      "Frequency",
      "Accountability",
      "Starting Date",
      "Ending Date"
    )
  )
    return "scorecards";

  if (has("Competency ID", "Competency Name", "Definition", "Criteria")) return "competencies";

  if (has("Competency Level ID", "Competency Level Name", "Definition", "Value"))
    return "competencyLevels";

  if (
    has(
      "Competency To Job ID",
      "Competency Name",
      "Job Name",
      "Job Group Name",
      "Competency Level Name"
    )
  )
    return "competencyToJob";

  if (has("Job Name")) return "jobDetails";

  // if (has("Competency To Training ID", "Competency Name", "Training Name"))
  //   return "competencyToTraining";

  if (has("Competency To Training ID", "Competency Name", "Training Name"))
    return "competencyToTraining";

  // if (
  //   has(
  //     "ID",
  //     "Tranining Name",
  //     "Employee Name",
  //     "Start Date",
  //     "End Date",
  //     "Expected Outcome Level",
  //     "Achieved Level"
  //   )
  // )
  //   return "learningAndDevelopment";

  if (
    has(
      "ID",
      "Tranining Name",
      "Employee Name",
      "Start Date",
      "End Date",
      "Expected Outcome Level",
      "Achieved Level"
    )
  )
    return "learningAndDevelopment";

  return null;
};

export const getImageSrc = (bytes) => {
  if (!bytes) return undefined;

  // If the data is already a Base64 string, return it directly
  if (typeof bytes === "string" && bytes.startsWith("data:image")) {
    return bytes;
  }

  // Convert binary data to Base64 (for API responses)
  return `data:image/png;base64,${bytes}`;
};

export const fieldStyles = {
  height: 35, // Adjust overall height
  "& .MuiInputBase-root": {
    height: 35, // Input field height
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSelect-select": {
    padding: 8, // Adjust padding inside select
  },
};

export const CustomKpiNode = ({ data }) => {
  const { label, incomingCount = 0 } = data;

  return (
    //Box of the node
    <Box
      sx={{
        // ... (rest of your existing styles)
        display: "flex", // Use flexbox
        alignItems: "center", // Vertical centering
        justifyContent: "center",
        width: "fit-content",
        minWidth: 160, //
        maxWidth: 250, //
        height: "40px",

        backgroundColor: "#D4E5E4",

        border: "none", // Rely entirely on shadow for definition
        borderRadius: 8, // Subtle roundness

        boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.15)", // Larger, softer shadow for a floating effect
        // Or a lighter, more diffused float:
        // boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.1)',

        padding: "12px 16px",
        textAlign: "center",
        whiteSpace: "normal",
        wordWrap: "break-word",
        // '&:hover': {
        //   backgroundColor: '#cbe0df',
        //   boxShadow: 6,
        //   cursor: 'pointer',
        // },
        "&:hover": {
          boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.2)",
          // backgroundColor: '#cbe0df',
          backgroundColor: "#95B8B7",
          transform: "translateY(-2px)",
          transition: "all 0.3s ease-out", // Make sure to add transition to the main sx for smooth animation
        },
        transition: "all 0.3s ease-out", // Added here
      }}
    >
      {/* Handle Point At The Top of the node for outputs */}

      <Handle
        key="top-source"
        type="source"
        position={Position.Top}
        id="top-source"
        style={{
          top: -8,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#000",
          width: 8,
          height: 8,
          borderRadius: "50%",
          border: "2px solid white",
        }}
      />

      {/* KPI label */}
      <Typography align="center" sx={{ color: "#000", fontWeight: "normal", fontSize: "13px" }}>
        {label}
      </Typography>

      {/* Handle Point At The Bottom of the node for inputs */}

      <Handle
        key="bottom-target"
        type="target"
        position={Position.Bottom}
        id="bottom-target"
        style={{
          bottom: -8,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#000",
          width: 8,
          height: 8,
          borderRadius: "50%",
          border: "2px solid white",
        }}
      />
    </Box>
  );
};

CustomKpiNode.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
    incomingCount: PropTypes.number,
  }).isRequired,
};

export const CustomKpiNodeForCeo = ({ data }) => {
  const { t, i18n } = useTranslation();
  const { label, incomingCount = 0, finalResult, resultColor, trend, onClick } = data;

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "up":
        return <TrendingUp color="success" />;
      case "down":
        return <TrendingDown color="error" />;
      case "flat":
        return <TrendingFlat color="warning" />;
      default:
        return <ErrorOutlineRounded color="disabled" />;
    }
  };

  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        if (typeof onClick === "function") onClick();
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "fit-content",
        minWidth: 160,
        maxWidth: 250,
        height: "100px",
        backgroundColor: "#F5F5DC",
        border: "2px solid #A7DBD8",
        borderRadius: "8px",
        // boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.08)', // Soft, subtle lift
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.06), 0px 8px 16px rgba(0, 0, 0, 0.1)", // Replace your current boxShadow
        padding: 0,
        textAlign: "center",
        whiteSpace: "normal",
        wordWrap: "break-word",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Handle (Top) */}
      <Handle
        key="top-source"
        type="source"
        position={Position.Top}
        id="top-source"
        style={{
          top: -8,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#000",
          width: 8,
          height: 8,
          borderRadius: "50%",
          border: "2px solid white",
        }}
      />

      {/* KPI Name Header */}
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#009688",
          // borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          borderBottom: "1px solid rgba(255, 255, 255, 0.2)", // Softer white line on teal
          // Add border-radius to the top corners of the header to match the parent Box
          borderTopLeftRadius: "6px", // Matches parent Box's borderRadius
          borderTopRightRadius: "6px", // Matches parent Box's borderRadius

          padding: "24px 6px 6px 6px",
        }}
      >
        <Typography
          align="center"
          sx={{
            color: "#000",
            fontWeight: 500,
            fontSize: "13px",
          }}
        >
          {label}
        </Typography>
      </Box>

      {/* Main Content */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", p: 1, px: 1.5 }}>
        {typeof finalResult === "number" ? (
          <Typography variant="caption" align="center" sx={{ fontSize: "12px", color: "#333333" }}>
            {t("user.kpiLinks.finalResult")}:{" "}
            <Box component="span" sx={{ color: "#009688", fontWeight: 600 }}>
              {finalResult.toFixed(2)}
            </Box>
          </Typography>
        ) : (
          <Typography variant="caption" align="center" sx={{ fontSize: "12px", color: "#333333" }}>
            {t("user.kpiLinks.finalResult")}:{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              —
            </Box>
          </Typography>
        )}

        <FiberManualRecord sx={{ color: resultColor }} />

        <Box sx={{ mb: 1 }}>{getTrendIcon(trend)}</Box>
      </Box>

      {/* Handle (Bottom) */}
      <Handle
        key="bottom-target"
        type="target"
        position={Position.Bottom}
        id="bottom-target"
        style={{
          bottom: -8,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#000",
          width: 8,
          height: 8,
          borderRadius: "50%",
          border: "2px solid white",
        }}
      />
    </Box>
  );
};

CustomKpiNodeForCeo.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
    incomingCount: PropTypes.number,
    finalResult: PropTypes.number,
    resultColor: PropTypes.string,
    trend: PropTypes.string,
    onClick: PropTypes.func,
  }).isRequired,
};

export const CustomEdgeWithDelete = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  data,
}) => {
  const positionWeight = 0.4; // tweak this value as needed
  const labelX = sourceX + (targetX - sourceX) * positionWeight;
  const labelY = sourceY + (targetY - sourceY) * positionWeight;

  const [edgePath] = getStraightPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
  });

  const onDeleteClick = (event) => {
    event.stopPropagation();
    data?.onDelete?.(id);
  };

  return (
    <>
      <path
        id={id}
        style={style}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
      />

      <foreignObject
        width={24}
        height={24}
        x={labelX - 12}
        y={labelY - 12}
        requiredExtensions="http://www.w3.org/1999/xhtml"
      >
        <IconButton
          onClick={onDeleteClick}
          style={{
            backgroundColor: "white",
            borderRadius: "50%",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
            width: 24,
            height: 24,
          }}
        >
          {/* <DeleteIcon fontSize="small" /> */}
          <DeleteIcon fontSize="small" style={{ color: "#e57373" }} />
        </IconButton>
      </foreignObject>
    </>
  );
};

CustomEdgeWithDelete.propTypes = {
  id: PropTypes.string.isRequired,
  sourceX: PropTypes.number.isRequired,
  sourceY: PropTypes.number.isRequired,
  targetX: PropTypes.number.isRequired,
  targetY: PropTypes.number.isRequired,
  sourcePosition: PropTypes.oneOf(["top", "right", "bottom", "left"]).isRequired,
  targetPosition: PropTypes.oneOf(["top", "right", "bottom", "left"]).isRequired,
  style: PropTypes.object,
  markerEnd: PropTypes.object,
  data: PropTypes.shape({
    onDelete: PropTypes.func,
  }),
};

export const fetchRiskMatrices = async (setRiskMatrices, setRiskMatrixChart) => {
  const riskMatricesData = await getRiskMatrices();

  if (riskMatricesData !== "error") {
    setRiskMatrices(riskMatricesData);

    const getRiskColor = (x, y) => {
      if ((x >= 2 && y === 3) || (x === 3 && y >= 2)) return "red";
      if ((x === 1 && y === 3) || (x === 2 && y === 2) || (x === 3 && y === 1)) return "yellow";
      return "gray";
    };

    const processedData = riskMatricesData.map((item) => ({
      label: item.matrixName,
      x: item.impact,
      y: item.likelihood,
      r: 5,
      backgroundColor: getRiskColor(item.impact, item.likelihood),
    }));

    setRiskMatrixChart([
      {
        label: "Risk Matrix",
        data: processedData,
        backgroundColor: processedData.map((point) => point.backgroundColor),
        borderColor: "black",
        borderWidth: 1,
      },
    ]);
  }
};

export const numberTextFieldStyling = {
  "& input[type=number]": {
    MozAppearance: "textfield", // Firefox
  },
  "& input[type=number]::-webkit-outer-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
};

export const drawZeroBarsPlugin = {
  id: "drawZeroBarsPlugin",
  afterDatasetsDraw(chart, args, pluginOptions) {
    const { ctx, chartArea, scales } = chart;
    const dataset = chart.data.datasets[0];

    dataset.data.forEach((value, index) => {
      if (value === 0) {
        const x = chart.getDatasetMeta(0).data[index].x;
        const y = scales.y?.getPixelForValue(0) || 0;
        const barWidth = chart.getDatasetMeta(0).data[index].width;

        ctx.save();
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x - barWidth / 2, y);
        ctx.lineTo(x + barWidth / 2, y);
        ctx.stroke();
        ctx.restore();
      }
    });
  },
};

export const scenarioTypes = () => {
  const types = [];
  const tenant = localStorage.getItem("tenant");

  // List of tenants allowed to see GAHAR
  const gaharTenants = ["b93e2f47c8d14a8b9f1d52e0ac7b63f2", "0a186506954e472aba3b994cc19613"];

  if (gaharTenants.includes(tenant)) {
    types.push("GAHAR Phase 1");
  }

  return types;
};

export const hasEditAccess = (modules, moduleName, employee) => {
  return employee?.role === 1 || modules?.[moduleName]?.toLowerCase() === "edit";
};

export const buttonStyles = (isSelected) => {
  const { primaryColor } = useAppTheme();

  return {
    borderColor: primaryColor,
    color: isSelected ? "#fff" : primaryColor,
    backgroundColor: isSelected ? primaryColor : "transparent",

    "&:hover": {
      backgroundColor: isSelected ? primaryColor : primaryColor,
      color: "#fff",
      borderColor: primaryColor,
    },

    "&:focus, &:focus-visible, &:focus-within": {
      backgroundColor: primaryColor,
      borderColor: primaryColor,
      color: "#fff !important",
      outline: "none",
      boxShadow: "none",
    },

    "&:active": {
      backgroundColor: primaryColor, // keeps it consistent while clicking
      color: "#fff",
    },

    "&.MuiButton-contained": {
      backgroundColor: primaryColor,
      color: "#fff",
      "&:hover": {
        backgroundColor: primaryColor,
      },
      "&:focus": {
        backgroundColor: primaryColor,
        boxShadow: "none",
        outline: "none",
      },
    },
  };
};

export const allowedTenantIds = [
  "NP",
  "CHG",
  "RAMZ",
  "HRHO",
  "HJ",
  "TAYARAH",
  "GAHAR",
  "CURA",
  "MEDTOWN",
  "MTOR",
  "SURGITECH",
  "TAQA",
  "DEMO",
  "NNC",
  "LIN",
];

const tenantKeyMap = {
  NP: "c9e2a7f5b1d4c8e0f3a6b9d2e7c1f5a8",
  CHG: "1f7d4b9a2c6e0f3d8a5b1c7e2f9d4a6b",
  RAMZ: "hCslTiz5o1Xk5YwSxkf611xZLwEAnK",
  HJ: "",
  TAYARAH: "e4b1c9d7a2f6e0c3b8d5a1f7c2e9b4d6",
  HRHO: "0a186506954e472aba3b994cc19613",
  GAHAR: "b93e2f47c8d14a8b9f1d52e0ac7b63f2",
  CURA: "5e1c9a7d2b4f6c8e0a3d1b7f9e2c4d8f",
  MEDTOWN: "a7f3d9b1c8e642f5d0b9a1c3e7f2d6b4",
  MTOR: "8c2f1d6b9a4e0f7c3d5b2a1e6f8d9b4a",
  SURGITECH: "a7c4e9d1b2f8c6e3d0a5f9b1c7e2d4a8",
  TAQA: "8b2f7c1d9e4a6f0c3d5b8a1e7c9f2d4a",
  DEMO: "7b3e9f1a6c4d8e2f0a5b7c9d3e1f6a81",
  NNC: "9c29bc6641724f67b32dce00dec48202",
  LIN: "3da416d5a8464f6786204dcc680446fe",
};

export function getTenantKey(tenantId) {
  return tenantKeyMap[tenantId.toUpperCase()] || "";
}

export function getTenantIdFromKey(key) {
  return Object.keys(tenantKeyMap).find((tenantId) => tenantKeyMap[tenantId] === key) || null;
}

// Renderer for Lists (Strengths/Weaknesses)
export const RenderBulletList = ({ items, type }) => {
  if (!items) return null;

  const normalizedItems = Array.isArray(items)
    ? items
    : items
        .split(";")
        .map((item) => item.trim())
        .filter(Boolean);

  if (normalizedItems.length === 0) return null;

  return (
    <List dense sx={{ py: 0 }}>
      {normalizedItems.map((text, index) => (
        <ListItem key={index} alignItems="flex-start" sx={{ px: 0 }}>
          <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
            <Circle
              sx={{
                fontSize: 8,
                color: type === "strength" ? "success.main" : "error.main",
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={text}
            primaryTypographyProps={{
              variant: "caption",
              color: "text.primary",
              sx: { lineHeight: 1.5 },
            }}
          />
        </ListItem>
      ))}
    </List>
  );
};

RenderBulletList.propTypes = {
  items: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  type: PropTypes.oneOf(["strength", "weakness"]).isRequired,
};

// Renderer for Complex Text (Suggestions) - Your Regex Logic
export const RenderRegexContent = ({ text, t }) => {
  if (!text) return null;

  // Split by regex to catch both "\n" and "\\n"
  const lines = text.split(/\\n|\n/).filter((line) => line.trim().length > 0);

  return (
    <Stack spacing={1.5} mt={1}>
      {lines.map((line, index) => {
        const cleanLine = line.trim();
        // Capture "Step X..." pattern
        const match = cleanLine.match(/^(\d+[^.:]*[.:])\s*(.*)/i);

        if (match) {
          const content = match[2];
          return (
            <Box key={index} display="flex" alignItems="flex-start">
              <CheckCircleOutline
                color="info"
                fontSize="small"
                sx={{ mt: 0.2, mr: 1, minWidth: "20px", width: "20px" }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {content}
              </Typography>
            </Box>
          );
        } else {
          return (
            <Box key={index} display="flex" alignItems="flex-start">
              <Circle sx={{ fontSize: 6, mt: 0.8, mr: 1.5, color: "grey.400" }} />
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {cleanLine}
              </Typography>
            </Box>
          );
        }
      })}
    </Stack>
  );
};

RenderRegexContent.propTypes = {
  text: PropTypes.string,
  t: PropTypes.func, // optional translation hook
};

// --- 2. THE INSIGHTS OVERLAY COMPONENT ---

export const InsightCard = ({ title, icon, color, children, onClose, visible }) => {
  const theme = useTheme();

  return (
    <Collapse in={visible}>
      <Paper
        elevation={4}
        sx={{
          width: 320,
          mb: 2,
          overflow: "hidden",
          // Glassmorphism effect
          backgroundColor: alpha(theme.palette.background.paper, 0.85),
          backdropFilter: "blur(8px)",
          border: `1px solid ${alpha(color, 0.3)}`,
          borderLeft: `4px solid ${color}`,
        }}
      >
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" p={1.5} pb={0.5}>
          <Box display="flex" alignItems="center" gap={1}>
            {icon}
            <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
              {title}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: "text.disabled" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box p={2} pt={0.5} sx={{ maxHeight: 200, overflowY: "auto" }}>
          {children}
        </Box>
      </Paper>
    </Collapse>
  );
};

InsightCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
};

// Helper to estimate text width based on character count
// Adjust '10' (pixels per char) and '30' (padding) based on your CSS font-size
export const estimateTextWidth = (text) => {
  const charCount = text ? text.length : 0;
  const minWidth = 220;
  const pixelsPerChar = 13; // Approx width of a character (depends on font)
  const padding = 40; // Internal padding of the card

  const estimated = charCount * pixelsPerChar + padding;
  return Math.max(minWidth, estimated);
};

// Helper for text width
const estimateTextWidthCsuite = (text) => {
  if (!text) return 180;
  const charCount = text.length;
  const minWidth = 160;
  const pixelsPerChar = 9;
  const padding = 40;
  const estimated = charCount * pixelsPerChar + padding;
  return Math.max(minWidth, estimated);
};

// ... estimateTextWidthCsuite ...

export const calculateCSuite = (jobs = [], options = {}) => {
  const {
    rootId = null,
    vSpacing = 150,
    hSpacing = 50,
    maxDepth = Infinity,
    maxCols = 9, // Used only when maxDepth === 1
  } = options;

  const safeId = (id) => (id !== null && id !== undefined ? String(id) : null);
  const targetRootId = safeId(rootId);
  const jobIds = new Set(jobs.map((j) => safeId(j.jobId)));

  const childMap = new Map();
  const nodeMap = new Map();
  const sizeMap = new Map();

  // 1. Build Maps & Widths
  jobs?.forEach((job) => {
    const jId = safeId(job.jobId);
    const pId = safeId(job.reportingToSolidId);

    const width =
      typeof estimateTextWidthCsuite === "function" ? estimateTextWidthCsuite(job.jobName) : 180;

    sizeMap.set(jId, width);

    if (pId && jobIds.has(pId)) {
      if (!childMap.has(pId)) childMap.set(pId, []);
      childMap.get(pId).push(job);
    }
  });

  // 2. Identify Subtree
  let jobsToProcess = [];
  if (targetRootId) {
    const subtree = new Map();
    const collect = (id, currentDepth) => {
      if (currentDepth > maxDepth) return;
      const sId = safeId(id);
      const job = jobs.find((j) => safeId(j.jobId) === sId);
      if (!job) return;
      subtree.set(sId, job);
      const children = childMap.get(sId) || [];
      children.forEach((child) => collect(child.jobId, currentDepth + 1));
    };
    collect(targetRootId, 0);
    jobsToProcess = Array.from(subtree.values());
  } else {
    jobsToProcess = jobs?.filter((j) => {
      const pId = safeId(j.reportingToSolidId);
      return !pId || !jobIds.has(pId);
    });
  }

  // 3. Recursive Position Calculation
  const calculatePositions = (jobId, x, y, currentDepth = 0) => {
    const sJobId = safeId(jobId);
    const myNodeWidth = sizeMap.get(sJobId) || 180;
    const myNodeHeight = 100;

    const children = (childMap.get(sJobId) || []).filter((child) => {
      if (targetRootId) return jobsToProcess.some((j) => safeId(j.jobId) === safeId(child.jobId));
      return true;
    });

    // BASE CASE: Leaf Node
    if (children.length === 0) {
      nodeMap.set(sJobId, { x, y, width: myNodeWidth });
      return { width: myNodeWidth, height: myNodeHeight };
    }

    // --- DECISION: GRID vs TREE LAYOUT ---
    // If we are only showing 1 level down, use the GRID (wrapping rows).
    // If we are showing deep levels, use STANDARD TREE (side-by-side).
    const useGridLayout = maxDepth === 1;

    let boundingBoxWidth = 0;
    let boundingBoxHeight = 0;

    if (useGridLayout) {
      // =================================================
      // OPTION A: GRID LAYOUT (For N-1 / Team View)
      // =================================================

      // 1. Organize children into Rows
      const rows = [];
      for (let i = 0; i < children.length; i += maxCols) {
        rows.push(children.slice(i, i + maxCols));
      }

      // 2. Calculate Max Width for EACH COLUMN
      const colWidths = new Array(maxCols).fill(0);
      rows.forEach((row) => {
        row.forEach((child, colIndex) => {
          const childWidth = sizeMap.get(safeId(child.jobId)) || 180;
          if (childWidth > colWidths[colIndex]) {
            colWidths[colIndex] = childWidth;
          }
        });
      });

      // 3. Position Children
      // Ensure children start below the parent (Y + Height + Gap)
      let currentRowY = y + myNodeHeight + vSpacing;

      rows.forEach((row) => {
        let currentRowX = x;
        let maxRowHeight = 0;

        row.forEach((child, colIndex) => {
          const colWidth = colWidths[colIndex];
          const childWidth = sizeMap.get(safeId(child.jobId)) || 180;

          // Align Center of Slot
          const centeredX = currentRowX + (colWidth - childWidth) / 2;

          // Store layout data for the Edge to use (to prevent intersection)
          const originalJob = jobs.find((j) => safeId(j.jobId) === safeId(child.jobId));
          if (originalJob) {
            originalJob._layoutData = { colWidth: colWidth };
          }

          const result = calculatePositions(child.jobId, centeredX, currentRowY, currentDepth + 1);
          maxRowHeight = Math.max(maxRowHeight, result.height);

          currentRowX += colWidth + hSpacing;
        });

        const actualRowWidth = currentRowX - x - hSpacing;
        if (actualRowWidth > boundingBoxWidth) boundingBoxWidth = actualRowWidth;

        boundingBoxHeight += maxRowHeight + vSpacing;
        currentRowY += maxRowHeight + vSpacing;
      });
      boundingBoxHeight -= vSpacing;
    } else {
      // =================================================
      // OPTION B: STANDARD TREE LAYOUT (For Deep Hierarchy)
      // =================================================

      let currentChildX = x;
      let maxChildrenHeight = 0;

      children.forEach((child) => {
        const nextY = y + myNodeHeight + vSpacing;

        // Recurse: Position child and get its total subtree width
        const result = calculatePositions(child.jobId, currentChildX, nextY, currentDepth + 1);

        // Accumulate width so the next sibling starts after this subtree ends
        currentChildX += result.width + hSpacing;
        maxChildrenHeight = Math.max(maxChildrenHeight, result.height);

        // Clear layout data (Edges in Tree mode usually don't need column width logic)
        const originalJob = jobs.find((j) => safeId(j.jobId) === safeId(child.jobId));
        if (originalJob) delete originalJob._layoutData;
      });

      // Remove last margin
      currentChildX -= hSpacing;

      boundingBoxWidth = currentChildX - x;
      boundingBoxHeight = maxChildrenHeight;
    }

    // --- PARENT CENTERING ---
    // Center Parent Horizontally over the bounding box of its children
    const parentX = x + boundingBoxWidth / 2 - myNodeWidth / 2;
    const parentY = y;

    nodeMap.set(sJobId, { x: parentX, y: parentY, width: myNodeWidth });

    return {
      width: Math.max(myNodeWidth, boundingBoxWidth),
      height: myNodeHeight + vSpacing + boundingBoxHeight,
    };
  };

  // 4. Execute
  let cursor = 0;
  const startJobs = targetRootId
    ? [jobs.find((j) => safeId(j.jobId) === targetRootId)].filter(Boolean)
    : jobsToProcess.filter(
        (j) => !j.reportingToSolidId || !jobIds.has(safeId(j.reportingToSolidId))
      );

  startJobs.forEach((job) => {
    const result = calculatePositions(job.jobId, cursor, 0);
    cursor += result.width + 150;
  });

  // 5. Edges
  const processedIds = new Set(nodeMap.keys());
  const edges = jobs
    .filter((j) => {
      const jId = safeId(j.jobId);
      const pId = safeId(j.reportingToSolidId);
      return pId && processedIds.has(jId) && processedIds.has(pId);
    })
    .map((j) => ({
      id: `e${j.jobId}`,
      source: j.reportingToSolidId.toString(),
      target: j.jobId.toString(),
      // Use Custom Edge if depth=1 (Grid), else Standard Smoothstep usually looks cleaner for deep trees
      // But keeping customOrgEdge is fine if logic handles it.
      type: "customOrgEdge",
      style: { stroke: "#9e9e9e", strokeWidth: 1.5 },
      pathOptions: { borderRadius: 0 },
    }));

  return { nodeMap, edges, processedJobs: jobsToProcess };
};

export const calculateOrgChartLayout = (jobs = [], options = {}) => {
  const { rootId = null, isJob = false, vSpacing = isJob ? 220 : 150, hSpacing = 50 } = options; // Removed fixed nodeWidth from defaults

  const jobIds = new Set(jobs.map((j) => j.jobId));
  const childMap = new Map();
  const nodeMap = new Map();

  // 1. Build Child Map & Pre-calculate Widths
  const sizeMap = new Map(); // Store calculated width for each job

  jobs?.forEach((job) => {
    // Calculate and store dynamic width
    const width = estimateTextWidth(job.jobName);
    sizeMap.set(job.jobId, width);

    if (job.reportingToSolidId && jobIds.has(job.reportingToSolidId)) {
      if (!childMap.has(job.reportingToSolidId)) {
        childMap.set(job.reportingToSolidId, []);
      }
      childMap.get(job.reportingToSolidId).push(job);
    }
  });

  // 2. Identify which jobs to process (Same as before)
  let jobsToProcess = [];
  if (rootId) {
    const subtree = new Map();
    const collect = (id) => {
      const job = jobs.find((j) => j.jobId === id);
      if (!job) return;
      subtree.set(id, job);
      (childMap.get(id) || []).forEach((child) => collect(child.jobId));
    };
    collect(rootId);
    jobsToProcess = Array.from(subtree.values());
  } else {
    jobsToProcess = jobs?.filter((j) => !j.reportingToSolidId || !jobIds.has(j.reportingToSolidId));
  }

  // 3. Recursive Position Calculation
  const calculatePositions = (jobId, x, y) => {
    // Get the specific width for this node
    const myNodeWidth = sizeMap.get(jobId) || 180;

    const children = (childMap.get(jobId) || []).filter((child) =>
      rootId ? jobsToProcess.some((j) => j.jobId === child.jobId) : true
    );

    // BASE CASE: Leaf Node
    if (children.length === 0) {
      nodeMap.set(jobId, { x, y, width: myNodeWidth }); // Store width in result too
      return { width: myNodeWidth };
    }

    // RECURSIVE STEP
    let childrenTotalWidth = 0;

    children.forEach((child) => {
      // Pass the current accumulated width as the starting X for the child
      const result = calculatePositions(child.jobId, x + childrenTotalWidth, y + vSpacing);
      childrenTotalWidth += result.width + hSpacing;
    });

    // Remove the last hSpacing because we don't need margin after the last child
    childrenTotalWidth -= hSpacing;

    // Center parent over children
    // Formula: Start X of children + (Total Width of Children / 2) - (Parent Width / 2)
    const parentX = x + childrenTotalWidth / 2 - myNodeWidth / 2;

    nodeMap.set(jobId, { x: parentX, y, width: myNodeWidth });

    // IMPORTANT: The width of this entire subtree is the max of the parent OR the children
    // If the parent has a huge name but 1 small child, the parent dictates the width.
    return { width: Math.max(myNodeWidth, childrenTotalWidth) };
  };

  // 4. Execute calculation
  let currentX = 0;
  const startJobs = rootId ? [jobs.find((j) => j.jobId === rootId)].filter(Boolean) : jobsToProcess;

  startJobs.forEach((job) => {
    const result = calculatePositions(job.jobId, currentX, 50);
    // Add extra spacing between separate trees
    currentX += result.width + 100;
  });

  // 5. Build Edges (Same as before)
  const processedIds = new Set(nodeMap.keys());
  const edges = jobs
    .filter(
      (j) =>
        j.reportingToSolidId && processedIds.has(j.jobId) && processedIds.has(j.reportingToSolidId)
    )
    .map((j) => ({
      id: `e${j.jobId}`,
      source: j.reportingToSolidId.toString(),
      target: j.jobId.toString(),
    }));

  return { nodeMap, edges, processedJobs: jobsToProcess };
};

export const getSafeColor = (color) => {
  const { backgroundColor } = useAppTheme();
  if (!color || color === "transparent")
    return backgroundColor != "transparent" ? backgroundColor : "#FFF"; // Fallback to white hex
  return color;
};

// ✅ Utility to safely validate or convert color strings
export const normalizeColor = (color) => {
  if (!color) return "transparent";

  if (color.toLowerCase() === "transparent") return "transparent";

  const isValid =
    /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color) ||
    /^rgba?\((\s*\d+\s*,){2,3}\s*[\d.]+\s*\)$/i.test(color) ||
    /^hsla?\(/i.test(color);

  if (isValid) return color;
  if (/^[0-9a-f]{6}$/i.test(color)) return `#${color}`;
  return DEFAULT_PRIMARY;
};

export const hiringReasonOptions = [
  "New Position",
  "Replacement",
  "Business Growth",
  "Restructuring",
  "Seasonal Demand",
  "Project-Based",
  "Internship Conversion",
  "Other",
];
